import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Users, Calendar, Layers } from "lucide-react";
import { api } from "@/lib/api";
import ModulePreview from "@/components/ModulePreview";
import PodcastSection from "@/components/PodcastSection";
import CommunitySection from "@/components/CommunitySection";

const heroImg =
    "https://images.pexels.com/photos/21415155/pexels-photo-21415155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const stats = [
    { k: "6", l: "Modules" },
    { k: "50+", l: "Templates" },
    { k: "K–12 → Higher Ed", l: "Built for faculty" },
    { k: "On-campus", l: "Hands-on workshops" },
];

export default function Landing() {
    const [courses, setCourses] = useState([]);
    useEffect(() => {
        api.get("/courses").then(({ data }) => setCourses(data.courses)).catch(() => {});
    }, []);

    return (
        <div className="bg-background">
            {/* HERO */}
            <section className="relative border-b border-black/10 overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0 grid-line opacity-50" />
                <div className="container-edge relative grid grid-cols-1 lg:grid-cols-12 gap-10 pt-16 pb-20 lg:pt-24 lg:pb-32">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1 border border-black/15 text-[11px] tracking-[0.22em] uppercase font-bold">
                            <span className="h-1.5 w-1.5 bg-klein rounded-full" />
                            AI courses & workshops for faculty
                        </div>
                        <h1
                            data-testid="hero-title"
                            className="mt-6 font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.92]"
                        >
                            Teach AI.<br />
                            Use AI.<br />
                            <span className="text-klein">Lead with it.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-muted-foreground">
                            Six practical modules and on-campus workshops that turn faculty and entire schools into
                            confident, ethical AI practitioners — without the hype.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/courses"
                                data-testid="hero-cta-courses"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors"
                            >
                                Browse the 6 modules <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/workshops"
                                data-testid="hero-cta-workshop"
                                className="inline-flex items-center gap-2 px-6 py-3 border border-black/20 text-sm font-semibold hover:bg-black hover:text-white transition-colors"
                            >
                                Book a campus workshop <ArrowUpRight size={16} />
                            </Link>
                        </div>

                        <dl className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-6 max-w-2xl">
                            {stats.map((s) => (
                                <div key={s.l}>
                                    <dt className="font-heading font-black text-3xl tracking-tighter">{s.k}</dt>
                                    <dd className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.l}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="aspect-[4/5] w-full bg-black overflow-hidden">
                            <img src={heroImg} alt="Modern campus architecture" className="w-full h-full object-cover grayscale contrast-110" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 bg-klein text-white p-6 max-w-[260px] hidden sm:block">
                            <p className="overline text-white/70">For schools</p>
                            <p className="mt-2 font-heading font-bold text-xl tracking-tight leading-tight">
                                Bulk pricing & on-site workshops available.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMMUNITY — soft secondary CTA right after the hero; does NOT replace hero CTAs */}
            <CommunitySection />

            {/* INTRO PODCAST */}
            <PodcastSection />

            {/* MODULE 1 FREE PREVIEW */}
            <ModulePreview />

            {/* MODULE BENTO */}
            <section className="border-b border-black/10" data-testid="modules-section">
                <div className="container-edge py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
                        <div className="lg:col-span-7">
                            <p className="overline">The curriculum</p>
                            <h2 className="mt-3 font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
                                Six modules. Zero filler.
                            </h2>
                        </div>
                        <p className="lg:col-span-4 lg:col-start-9 text-muted-foreground self-end">
                            Buy one. Buy the bundle. License the whole school. Each module ships with worksheets,
                            templates, and the exact prompts faculty are using right now.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-black/10 border border-black/10">
                        {courses.map((c, idx) => {
                            // tetris span pattern
                            const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-7", "md:col-span-5"];
                            const span = spans[idx % spans.length];
                            return (
                                <div
                                    key={c.id}
                                    data-testid={`module-card-${c.id}`}
                                    className={`${span} bg-white p-8 lg:p-10 group hover:bg-klein hover:text-white transition-colors duration-300`}
                                >
                                    <div className="flex items-start justify-between">
                                        <span className="font-mono text-xs tracking-widest text-klein group-hover:text-white">
                                            MODULE / {c.number}
                                        </span>
                                        <span className="text-xs uppercase tracking-widest opacity-60">{c.duration}</span>
                                    </div>
                                    <h3 className="mt-6 font-heading font-bold text-3xl lg:text-4xl tracking-tight leading-[1.05]">
                                        {c.title}
                                    </h3>
                                    <p className="mt-2 text-sm uppercase tracking-widest opacity-70">{c.tagline}</p>
                                    <p className="mt-5 text-base leading-relaxed opacity-80">{c.description}</p>
                                    <div className="mt-8 flex items-center justify-between">
                                        <span className="font-heading font-black text-2xl">${c.price}</span>
                                        <Link
                                            to="/courses"
                                            data-testid={`module-cta-${c.id}`}
                                            className="inline-flex items-center gap-1 text-sm font-semibold underline-offset-4 hover:underline"
                                        >
                                            Buy or learn more <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section className="border-b border-black/10 bg-secondary">
                <div className="container-edge py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="overline">Who it's for</p>
                        <h2 className="mt-3 font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
                            Faculty.<br />Departments.<br />Whole schools.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
                        {[
                            { id: "individual", icon: Users, t: "Individual faculty", d: "Buy a module or the full bundle. Self-paced. Yours forever." },
                            { id: "departments", icon: Layers, t: "Departments", d: "Bulk seats with shared templates and a unified prompting framework." },
                            { id: "site-license", icon: Calendar, t: "School site license", d: "Up to 50 seats with all 6 modules. One invoice. One rollout." },
                            { id: "workshops", icon: Sparkles, t: "On-campus workshops", d: "Hands-on day with your team. We come to you." },
                        ].map((b) => (
                            <div key={b.id} className="bg-white p-8 lg:p-10">
                                <b.icon className="text-klein" size={28} />
                                <h3 className="mt-5 font-heading font-bold text-xl tracking-tight">{b.t}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="border-b border-black/10">
                <div className="container-edge py-20 lg:py-28">
                    <p className="overline">Voices</p>
                    <h2 className="mt-3 font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                        From the people who teach.
                    </h2>
                    <figure id="platt-college-testimonial" className="mt-12 bg-white border border-black/10 border-l-[6px] border-l-klein px-5 py-7 sm:px-10 sm:py-10" data-testid="platt-college-testimonial">
                        <blockquote className="font-heading text-xl leading-[1.45] sm:text-3xl sm:leading-snug tracking-tight max-w-4xl">
                            &ldquo;Thank you, Dr. Genevieve Dobson, DBA, for your partnership in delivering a highly effective faculty training session. We&rsquo;re excited to leverage innovative tools and approaches to strengthen student outcomes, and we look forward to continuing to work together to advance faculty development.&rdquo;
                        </blockquote>
                        <figcaption className="mt-8">
                            <div className="font-semibold text-base">Platt College (via LinkedIn)</div>
                            <div className="text-[13px] text-muted-foreground uppercase tracking-widest mt-1">AI faculty development workshop, all four California campuses, Jan 2026</div>
                        </figcaption>
                    </figure>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-klein text-white" data-testid="bottom-cta">
                <div className="container-edge py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                            Ready to bring AI to your campus?
                        </h2>
                        <p className="mt-4 text-white/80 max-w-xl">
                            Schedule a hands-on workshop or get the full faculty bundle today. We'll reply within one business day.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
                        <Link
                            to="/workshops"
                            data-testid="cta-book-workshop"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-klein text-sm font-semibold hover:bg-black hover:text-white transition-colors"
                        >
                            Book a workshop <ArrowUpRight size={16} />
                        </Link>
                        <Link
                            to="/courses"
                            data-testid="cta-buy-bundle"
                            className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-sm font-semibold hover:bg-white hover:text-klein transition-colors"
                        >
                            Buy the bundle <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
